import {
  ISSUE_STATUS_LABELS,
  PROJECT_STATUS_LABELS,
} from '@company/shared'
import {
  Search,
  X,
  Loader2,
  FolderKanban,
  CheckSquare,
  Users,
  Clock,
  PlusCircle,
  FolderPlus,
  UserPlus,
  DollarSign,
  ChevronRight,
} from 'lucide-react'
import { useState, useCallback, useEffect, useRef } from 'react'

import { useCurrentUser ,
  useGlobalSearch,
  useSearchHistory,
  useSearchResultItems,
  useQuickActions,
} from '@/hooks'
import { cn } from '@/lib/utils'

import type {
  SearchCategory,
  SearchResultItem,
  QuickAction,
} from '@/hooks'

// ==================== Types ====================

interface SearchDialogProps {
  isOpen: boolean
  onClose: () => void
}

// ==================== Constants ====================

const CATEGORY_LABELS: Record<SearchCategory, string> = {
  projects: '项目',
  issues: '任务',
  employees: '员工',
}

const CATEGORY_ICONS: Record<SearchCategory, typeof FolderKanban> = {
  projects: FolderKanban,
  issues: CheckSquare,
  employees: Users,
}

const ACTION_ICONS: Record<string, typeof PlusCircle> = {
  'plus-circle': PlusCircle,
  clock: Clock,
  'folder-plus': FolderPlus,
  'user-plus': UserPlus,
  'dollar-sign': DollarSign,
}

const STATUS_COLORS: Record<string, string> = {
  // 项目状态
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  completed: 'bg-blue-100 text-blue-700',
  archived: 'bg-gray-100 text-gray-500',
  // 任务状态
  backlog: 'bg-gray-100 text-gray-700',
  todo: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  code_review: 'bg-purple-100 text-purple-700',
  testing: 'bg-orange-100 text-orange-700',
  done: 'bg-green-100 text-green-700',
  blocked: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
}

// Combine project and issue status labels
const STATUS_LABELS: Record<string, string> = {
  ...PROJECT_STATUS_LABELS,
  ...ISSUE_STATUS_LABELS,
}

// ==================== Component ====================

export function SearchDialog({ isOpen, onClose }: SearchDialogProps) {
  const { data: user } = useCurrentUser()
  const inputRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)

  const { data: results, isLoading, isFetching } = useGlobalSearch(query)
  const { history, addSearch, clearHistory } = useSearchHistory()
  const items = useSearchResultItems(results)
  const quickActions = useQuickActions(user?.role ?? 'developer')

  // 重置状态
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      // 延迟聚焦，等待动画完成
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // 重置选中索引
  useEffect(() => {
    setSelectedIndex(0)
  }, [query, results])

  // 点击外部关闭
  useEffect(() => {
    if (!isOpen) {return}

    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  // 获取当前可选项列表
  const getSelectableItems = useCallback((): Array<{ type: 'result' | 'history' | 'action'; item: SearchResultItem | string | QuickAction }> => {
    if (query.length >= 2 && items.length > 0) {
      return items.map((item) => ({ type: 'result' as const, item }))
    }

    if (query.length < 2) {
      const selectables: Array<{ type: 'result' | 'history' | 'action'; item: SearchResultItem | string | QuickAction }> = []

      // 最近搜索
      history.forEach((h) => selectables.push({ type: 'history', item: h }))

      // 快捷操作
      quickActions.forEach((a) => selectables.push({ type: 'action', item: a }))

      return selectables
    }

    return []
  }, [query, items, history, quickActions])

  const selectableItems = getSelectableItems()

  // 处理选择
  const handleSelect = useCallback(
    (item: { type: 'result' | 'history' | 'action'; item: SearchResultItem | string | QuickAction }) => {
      if (item.type === 'result') {
        const result = item.item as SearchResultItem
        addSearch(query)
        onClose()
        window.location.href = result.path
      } else if (item.type === 'history') {
        setQuery(item.item as string)
      } else if (item.type === 'action') {
        const action = item.item as QuickAction
        onClose()
        window.location.href = action.path
      }
    },
    [query, addSearch, onClose]
  )

  // 键盘导航
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const maxIndex = selectableItems.length - 1

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex))
          break
        case 'Enter':
          e.preventDefault()
          if (selectableItems[selectedIndex]) {
            handleSelect(selectableItems[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
        case 'Tab':
          e.preventDefault()
          // Tab 切换分类（简化实现：循环选择）
          setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0))
          break
      }
    },
    [selectableItems, selectedIndex, handleSelect, onClose]
  )

  // 高亮匹配文本
  const highlightText = useCallback((text: string, highlight: string) => {
    if (!highlight || highlight.length < 2) {return text}

    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    )
  }, [])

  // 按分类分组结果
  const groupedResults = items.reduce(
    (acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = []
      }
      acc[item.category].push(item)
      return acc
    },
    {} as Record<SearchCategory, SearchResultItem[]>
  )

  const showResults = query.length >= 2
  const showEmpty = showResults && !isLoading && items.length === 0
  const showHistory = !showResults && history.length > 0
  const showQuickActions = !showResults && quickActions.length > 0

  if (!isOpen) {return null}

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50">
      <div
        ref={dialogRef}
        className="w-full max-w-2xl rounded-lg bg-background shadow-lg overflow-hidden"
      >
        {/* 搜索输入框 */}
        <div className="flex items-center border-b px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="搜索项目、任务、员工..."
            className="flex-1 bg-transparent border-0 outline-none text-base placeholder:text-muted-foreground"
          />
          {(isLoading || isFetching) && (
            <Loader2 className="h-5 w-5 text-muted-foreground animate-spin mr-2" />
          )}
          {query && (
            <button
              className="p-1 rounded hover:bg-muted"
              onClick={() => setQuery('')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="ml-2 text-xs text-muted-foreground border rounded px-1.5 py-0.5">
            ESC
          </div>
        </div>

        {/* 搜索结果 */}
        <div className="max-h-[400px] overflow-y-auto">
          {/* 搜索结果列表 */}
          {showResults && !showEmpty && (
            <div className="py-2">
              {(Object.keys(groupedResults) as SearchCategory[]).map((category) => (
                <div key={category}>
                  <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                    <span>{CATEGORY_LABELS[category]}</span>
                    {results?.totals[category] && results.totals[category] > groupedResults[category].length && (
                      <span className="text-xs text-muted-foreground">
                        共 {results.totals[category]} 条
                      </span>
                    )}
                  </div>
                  {groupedResults[category].map((item) => {
                    const globalIndex = items.indexOf(item)
                    const Icon = CATEGORY_ICONS[category]
                    return (
                      <button
                        key={item.id}
                        className={cn(
                          'w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-accent transition-colors',
                          globalIndex === selectedIndex && 'bg-accent'
                        )}
                        onClick={() => handleSelect({ type: 'result', item })}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                      >
                        <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground font-mono">
                              {item.code}
                            </span>
                            <span className="truncate">
                              {highlightText(item.title, query)}
                            </span>
                          </div>
                          {item.subtitle && (
                            <div className="text-xs text-muted-foreground truncate">
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                        {item.status && (
                          <span
                            className={cn(
                              'text-xs px-2 py-0.5 rounded-full flex-shrink-0',
                              STATUS_COLORS[item.status] ?? 'bg-gray-100 text-gray-700'
                            )}
                          >
                            {STATUS_LABELS[item.status] ?? item.status}
                          </span>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          )}

          {/* 空状态 */}
          {showEmpty && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>未找到匹配结果</p>
              <p className="text-sm mt-1">尝试使用其他关键词搜索</p>
            </div>
          )}

          {/* 最近搜索 */}
          {showHistory && (
            <div className="py-2">
              <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                <span>最近搜索</span>
                <button
                  className="text-xs hover:text-foreground"
                  onClick={clearHistory}
                >
                  清除
                </button>
              </div>
              {history.map((h, idx) => (
                <button
                  key={h}
                  className={cn(
                    'w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-accent transition-colors',
                    idx === selectedIndex && 'bg-accent'
                  )}
                  onClick={() => handleSelect({ type: 'history', item: h })}
                  onMouseEnter={() => setSelectedIndex(idx)}
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{h}</span>
                </button>
              ))}
            </div>
          )}

          {/* 快捷操作 */}
          {showQuickActions && (
            <div className="py-2 border-t">
              <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                快捷操作
              </div>
              {quickActions.map((action, idx) => {
                const globalIndex = history.length + idx
                const Icon = ACTION_ICONS[action.icon] ?? PlusCircle
                return (
                  <button
                    key={action.id}
                    className={cn(
                      'w-full px-4 py-2 flex items-center gap-3 text-left hover:bg-accent transition-colors',
                      globalIndex === selectedIndex && 'bg-accent'
                    )}
                    onClick={() => handleSelect({ type: 'action', item: action })}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{action.label}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                  </button>
                )
              })}
            </div>
          )}

          {/* 提示 */}
          {!showResults && !showHistory && !showQuickActions && (
            <div className="py-12 text-center text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>输入至少 2 个字符开始搜索</p>
            </div>
          )}
        </div>

        {/* 底部快捷键提示 */}
        <div className="border-t px-4 py-2 flex items-center gap-4 text-xs text-muted-foreground bg-muted/50">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px]">↑</kbd>
            <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px]">↓</kbd>
            导航
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px]">Enter</kbd>
            选择
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-background border rounded text-[10px]">Esc</kbd>
            关闭
          </span>
        </div>
      </div>
    </div>
  )
}
